import * as React from 'react';
import styles from './Item.scss';
import { default as globalStyles } from '~/styles/global.scss';
import { ICategories, IInventoryList, IInventoryState } from '~/interfaces';
import { Item } from '~/interfaces';
import DivTooltip from '@wg/wows-react-uikit/DivTooltip';
import classNames from 'classnames';
import { Crew, Doll, Ensign, Permoflages, Vehicle } from '~/vortex';
import { getLocalPath, isInGameBrowser } from '~/helpers/utils';
import { getVehicleTypeIcon } from '~/helpers/vehiclesApi';
import { getLevelFromRomanFormat } from '~/helpers/levels';
import Tooltip from '~/Components/Tooltip/Tooltip';
import { t } from '~/helpers/localization';
import { CREW_TYPES, DOLLS_TYPES, DWH_EVENTS, ENSIGN_TYPES, INVENTORY_KEY_BY_CATEGORY } from '~/helpers/consts';
import { getVehicles } from '~/Components/Item/permoflagesShips';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DIALOG_ITEM_INFO } from '~/Components/Dialog/DialogManager';
import { State } from '~/Reducers';
import { isAuthorizedUser } from '~/config/user';
import { CREWS_CATEGORY, DOLLS_CATEGORY, ENSIGNS_CATEGORY, PERMOFLAGES_CATEGORY, VEHICLE_CATEGORY, showDialog } from '~/Actions/ActionCategory';
import { playButtonDialogClickSound } from '~/helpers/audioApi';
import DogTagBackground from '~/Components/DogTagBackground/DogTagBackground';
import dwhExport from '~/helpers/dwhExport';

interface IItem {
    category: ICategories;
    item: Item;
}

const isEnabledItem = (item: Item, category: ICategories, inventory: IInventoryState): boolean => {
    if (!isAuthorizedUser()) {
        return true;
    }

    const inventoryKey = INVENTORY_KEY_BY_CATEGORY[category] as IInventoryList;
    const items = (inventory[inventoryKey] as string[]) || [];

    return items.includes(item.id);
};

interface IState {
    inventory: IInventoryState;
}

const stateSelector = (state: State): IState => {
    return {
        inventory: state.ReducerApp.inventory,
    };
};

const ItemComp = (props: IItem) => {
    const appState = useSelector<State, IState>(stateSelector, shallowEqual);
    const dispatch = useDispatch();

    let isGoldName = false;
    let background = null;
    let isIconContain = false;
    let title = props.item.title;
    const isNeedToRenderIconWater = props.category === VEHICLE_CATEGORY;
    let icon = null;
    let titleIcon = null;
    let tooltipBody = null;
    let type = null;
    const isEnabled = isEnabledItem(props.item, props.category, appState.inventory);
    let isUnique = props.item.isUniqueItem;

    if (props.category === VEHICLE_CATEGORY || props.category === CREWS_CATEGORY) {
        const data = props.item as Crew | Vehicle;
        background = data.nation?.icons?.small;
        isIconContain = true;
    }

    if (props.category === CREWS_CATEGORY) {
        const crew = props.item as Crew;
        icon = crew.icons?.[0]?.default;
        isUnique = crew.type === CREW_TYPES.UNIQUE;
        type = crew.type;

        let subTitle = t('Командир');
        if (crew.type === CREW_TYPES.UNIQUE) {
            subTitle = t('Уникальный командир');
        } else if (crew.type === CREW_TYPES.ADVANCED) {
            subTitle = t('Улучшенный командир');
        } else if (crew.type === CREW_TYPES.SPECIAL) {
            subTitle = t('Особый командир');
        }

        const subTitleContent = <span className={classNames(styles.itemSpecialTitle, styles[crew.type], styles[props.category])}>{subTitle}</span>;

        title = (
            <span
                className={classNames({
                    [styles.gold]: crew.type === CREW_TYPES.UNIQUE,
                })}
            >
                {crew.title}
            </span>
        );

        tooltipBody = (
            <Tooltip
                icon={icon}
                background={crew.nation?.icons.small}
                preTitle={subTitleContent}
                title={title}
                isEnabled={isEnabled}
                isIconContain={true}
                isUniqueItem={isUnique}
                isRareItem={props.item.isRareItem}
                category={CREWS_CATEGORY}
                description={null}
                type={type}
            />
        );
    } else if (props.category === DOLLS_CATEGORY) {
        const doll = props.item as Doll;
        if (doll.type === DOLLS_TYPES.PATCH || doll.type === DOLLS_TYPES.SYMBOL) {
            icon = isInGameBrowser && doll.icons.localSmall ? getLocalPath(doll.icons.localSmall) : doll.icons.small;
        } else {
            icon = <DogTagBackground doll={doll} className={styles.dogTagBg} />;
        }

        tooltipBody = (
            <Tooltip
                icon={icon}
                title={title}
                preTitle={doll.type === DOLLS_TYPES.PATCH ? t('Нашивка') : doll.type === DOLLS_TYPES.SYMBOL ? t('Символ') : t('Форма')}
                isEnabled={isEnabled}
                isIconContain={true}
                isMoreInfo={true}
                isUniqueItem={isUnique}
                isRareItem={props.item.isRareItem}
                category={DOLLS_CATEGORY}
                description={null}
            />
        );
    } else if (props.category === ENSIGNS_CATEGORY) {
        const ensign = props.item as Ensign;
        icon = isInGameBrowser && ensign.icons.localDefault ? getLocalPath(ensign.icons.localDefault) : ensign.icons.default;

        tooltipBody = (
            <Tooltip
                icon={icon}
                title={title}
                preTitle={ENSIGN_TYPES[ensign.categoryType]}
                description={ensign.description}
                isEnabled={isEnabled}
                isIconContain={false}
                isUniqueItem={isUnique}
                isRareItem={props.item.isRareItem}
                category={ENSIGNS_CATEGORY}
            />
        );
    } else if (props.category === PERMOFLAGES_CATEGORY) {
        const camo = props.item as Permoflages;
        const classNameTitle = classNames(globalStyles.tooltipVehicleTitle);
        const camoVehicle = camo.vehicles?.[0];

        icon = isInGameBrowser && camo.icons.localDefault ? getLocalPath(camo.icons.localDefault) : camo.icons.default;

        tooltipBody = (
            <Tooltip
                icon={icon}
                title={camo.title}
                description={null}
                preTitle={t('Постоянный камуфляж')}
                content={getVehicles(camo.vehicles || [])}
                isEnabled={isEnabled}
                isIconContain={false}
                isUniqueItem={isUnique}
                isRareItem={props.item.isRareItem}
                category={PERMOFLAGES_CATEGORY}
            />
        );

        title = (
            <>
                {camo.title}:<span className={classNameTitle}>{camoVehicle?.vehicle.titleShort}</span>
            </>
        );
    } else if (props.category === VEHICLE_CATEGORY) {
        const ship = props.item as Vehicle;
        const level = getLevelFromRomanFormat(ship.level);

        isGoldName = ship.isPremium || ship.isSpecial;
        titleIcon = getVehicleTypeIcon(ship);
        title = level + ' ' + ship.titleShort;
        icon = isInGameBrowser && ship.icons.localSmall ? getLocalPath(ship.icons.localSmall) : ship?.icons?.small;

        tooltipBody = (
            <Tooltip
                title={title}
                background={ship.nation?.icons.small}
                icon={icon}
                titleIcon={getVehicleTypeIcon(ship)}
                isGold={ship.isPremium || ship.isSpecial}
                waterIcon={true}
                description={ship.description}
                preTitle={ship.type?.title}
                isEnabled={isEnabled}
                isExclusive={false}
                isIconContain={true}
                isMoreInfo={true}
                isUniqueItem={isUnique}
                isRareItem={props.item.isRareItem}
                category={VEHICLE_CATEGORY}
            />
        );
    }

    const isEnableClick = props.category === VEHICLE_CATEGORY || props.category === DOLLS_CATEGORY;

    const classNameItem = classNames(styles.item, {
        [styles.isUnique]: props.item.isUniqueItem,
        [styles.isRare]: props.item.isRareItem,
        [styles.itemNotEnabled]: !isEnabled,
        [styles.dolls]: props.category === DOLLS_CATEGORY,
        [styles.itemActive]: isEnableClick,
    });

    const classNameWrap = classNames(styles.iconWrap, {
        [styles.overflowHidden]: props.category === CREWS_CATEGORY,
    });

    const classNameIcon = classNames(styles.icon, 'lazy', {
        [styles.iconContain]: isIconContain,
    });

    const classNameInfo = classNames(styles.info, {
        [styles.gold]: isGoldName,
        [styles.vehicle]: props.category === VEHICLE_CATEGORY,
    });

    const onClick = () => {
        if (!isEnableClick) {
            return;
        }

        playButtonDialogClickSound();

        dwhExport.send(DWH_EVENTS.ITEM_PREVIEW, { category: props.category, cd: props.item.id });

        dispatch(
            showDialog({
                popupName: DIALOG_ITEM_INFO,
                popupData: {
                    itemId: props.item.id,
                    category: props.category,
                },
            }),
        );
    };

    const getTitle = () => {
        switch (props.category) {
            case CREWS_CATEGORY:
                const crew = props.item as Crew;
                return <span className={classNames(styles.itemSpecialTitle, styles[props.category], styles[crew.type], styles.itemTitle)}>{crew.title}</span>;

            default:
                return <span className={styles.title}>{title}</span>;
        }
    };

    return (
        <DivTooltip tooltipBody={tooltipBody} className={styles.divTooltip}>
            <div className={classNameItem} onClick={onClick}>
                <div className={styles.itemContent}>
                    {background && <div className={classNames(styles.background, 'lazy')} data-background={background} />}
                    <div className={classNameWrap}>
                        {isNeedToRenderIconWater && <div className={styles.iconWater} />}
                        {typeof icon === 'string' ? <div className={classNameIcon} data-background={icon} /> : icon}
                        {type && <div className={classNames(styles.itemTypeMark, styles[props.category], styles[type])} />}
                    </div>
                    <div className={classNameInfo}>
                        {titleIcon && <span className={classNames(styles.iconTitle, 'lazy')} data-background={titleIcon} />}
                        {getTitle()}
                    </div>
                </div>
            </div>
        </DivTooltip>
    );
};

export default ItemComp;
